import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "../../components/header";
import PlaylistCard from "../../components/playlist/PlaylistCard";
import PlaylistViewer from "../../components/playlist/PlaylistViewer";
import CreatePlaylistModal from "../../components/playlist/CreatePlaylistModal";
import {
  getMyPlaylists,
  getSavedPlaylists,
  getPublicPlaylists,
  getPlaylist,
} from "../../functions/playlist";
import "./style.css";

export default function Playlists() {
  const { user } = useSelector((state) => ({ ...state }));
  const { type, playlistId } = useParams();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [publicPlaylists, setPublicPlaylists] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [type]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (playlistId) {
      loadPlaylist(playlistId);
    } else {
      setCurrentPlaylist(null);
    }
  }, [playlistId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [myRes, savedRes, publicRes] = await Promise.all([
        getMyPlaylists(user.token),
        getSavedPlaylists(user.token),
        getPublicPlaylists(user.token),
      ]);

      if (myRes.status === "ok") setPlaylists(myRes.data);
      if (savedRes.status === "ok") setSavedPlaylists(savedRes.data);
      if (publicRes.status === "ok") setPublicPlaylists(publicRes.data);
    } catch (error) {
      console.error("Erro ao carregar playlists:", error);
    }
    setLoading(false);
  };

  const loadPlaylist = async (id) => {
    const res = await getPlaylist(id, user.token);
    if (res.status === "ok") {
      setCurrentPlaylist(res.data);
    }
  };

  const handlePlaylistCreated = (newPlaylist) => {
    if (editingPlaylist) {
      setPlaylists(
        playlists.map((p) => (p._id === newPlaylist._id ? newPlaylist : p))
      );
    } else {
      setPlaylists([newPlaylist, ...playlists]);
    }
    setEditingPlaylist(null);
    setShowCreateModal(false);
  };

  const handlePlaylistClick = (playlist) => {
    navigate(`/playlists/watch/${playlist._id}`);
  };

  const getCurrentPlaylists = () => {
    switch (type) {
      case "saved":
        return savedPlaylists;
      case "discover":
        return publicPlaylists;
      default:
        return playlists;
    }
  };

  const getPageTitle = () => {
    switch (type) {
      case "saved":
        return "Playlists Salvas";
      case "discover":
        return "Descobrir";
      default:
        return "Minhas Playlists";
    }
  };

  // Se estiver vendo uma playlist específica
  if (currentPlaylist) {
    return (
      <>
        <Header page="playlists" />
        <div className="playlists">
          <div className="playlists_left">
            <div className="playlists_left_header">
              <h3>Playlists</h3>
            </div>
            <div className="playlists_left_wrap">
              <div
                className="playlists_menu_item"
                onClick={() => navigate("/playlists")}
                style={{ cursor: "pointer" }}
              >
                <div className="menu_icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </div>
                <span>Voltar</span>
              </div>
            </div>
          </div>
          <div className="playlists_right" style={{ maxWidth: "1000px" }}>
            <PlaylistViewer
              playlist={currentPlaylist}
              onPlaylistUpdate={() => loadPlaylist(currentPlaylist._id)}
              isOwner={currentPlaylist.user?._id === user.id}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header page="playlists" />
      <div className="playlists">
        <div className="playlists_left">
          <div className="playlists_left_header">
            <h3>Playlists</h3>
          </div>
          <div className="playlists_left_wrap">
            <Link
              to="/playlists"
              className={`playlists_menu_item ${type === undefined ? "active" : ""}`}
            >
              <div className="menu_icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
                </svg>
              </div>
              <span>Minhas Playlists</span>
              {playlists.length > 0 && (
                <span className="playlists_count">{playlists.length}</span>
              )}
            </Link>
            <Link
              to="/playlists/saved"
              className={`playlists_menu_item ${type === "saved" ? "active" : ""}`}
            >
              <div className="menu_icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z" />
                </svg>
              </div>
              <span>Salvas</span>
              {savedPlaylists.length > 0 && (
                <span className="playlists_count">{savedPlaylists.length}</span>
              )}
            </Link>
            <Link
              to="/playlists/discover"
              className={`playlists_menu_item ${type === "discover" ? "active" : ""}`}
            >
              <div className="menu_icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 10.9c-.61 0-1.1.49-1.1 1.1s.49 1.1 1.1 1.1c.61 0 1.1-.49 1.1-1.1s-.49-1.1-1.1-1.1zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm2.19 12.19L6 18l3.81-8.19L18 6l-3.81 8.19z" />
                </svg>
              </div>
              <span>Descobrir</span>
            </Link>
          </div>
        </div>

        <div className="playlists_right">
          <div className="playlists_right_header">
            <h2>{getPageTitle()}</h2>
            {type === undefined && (
              <button
                className="create_playlist_btn"
                onClick={() => setShowCreateModal(true)}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
                Criar Playlist
              </button>
            )}
          </div>

          {loading ? (
            <div className="playlists_loading">
              <div className="spinner"></div>
            </div>
          ) : getCurrentPlaylists().length === 0 ? (
            <div className="playlists_empty">
              <svg viewBox="0 0 24 24">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
              </svg>
              <h3>
                {type === "saved"
                  ? "Nenhuma playlist salva"
                  : type === "discover"
                  ? "Nenhuma playlist encontrada"
                  : "Nenhuma playlist criada"}
              </h3>
              <p>
                {type === "saved"
                  ? "Salve playlists de outros usuários para vê-las aqui"
                  : type === "discover"
                  ? "Playlists dos seus amigos aparecerão aqui"
                  : "Crie sua primeira playlist para organizar seus vídeos favoritos"}
              </p>
              {type === undefined && (
                <button
                  className="create_playlist_btn"
                  onClick={() => setShowCreateModal(true)}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  Criar Playlist
                </button>
              )}
            </div>
          ) : (
            <div className="playlists_grid">
              {getCurrentPlaylists().map((playlist) => (
                <PlaylistCard
                  key={playlist._id}
                  playlist={playlist}
                  onClick={() => handlePlaylistClick(playlist)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreatePlaylistModal
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingPlaylist(null);
        }}
        onPlaylistCreated={handlePlaylistCreated}
        editPlaylist={editingPlaylist}
      />
    </>
  );
}
